import React, { memo, useEffect, useCallback, useMemo } from 'react';
import { FormBase, InputField, Validator, SelectField } from '../../components';
import { Row, Col } from 'reactstrap';
import BasicRowColGrid from '../../utility/BasicRowColGrid';
import strings from '../../globalization';
import { reduxForm, Field, initialize, change, reset } from 'redux-form';
import { debounce } from 'lodash';
import {
  trackingSystemForm,
  TRACKING_SYSTEM_FORM,
  trackingSystemFormReset,
  trackingSystemContactList,
  trackingSystemContactListReset
} from '../../redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import utility from '../../utility';

import { useHistory } from 'react-router-dom';
import TrackingSystemContact from './TrackingSystemContactList';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import consts from '../../consts';

const TrackingSystemForm = memo(props => {
  const history = useHistory();
  const { params } = props.match;
  const dispatch = useDispatch();
  const { data: initialValues = {}, success, info, err } = useSelector(
    state => state.trackingSystemFormData.data
  );
  const trackingSystemContactData = useSelector(
    state => state.comboTypes.TrackingSystemContact
  );

  const onChange = debounce((event, newValue, previousValue, name) => {
    dispatch(change('TrackingSystem', name, newValue));
  }, 100);

  const shouldGoBack = useMemo(
    () =>
      success &&
      [
        'RecordSavedSuccessfully',
        'RecordUpdatedSuccessfully',
        'DeleteSuccess'
      ].includes(info),
    [success, info]
  );

  useEffect(() => {
    if (shouldGoBack) {
      dispatch(trackingSystemFormReset());
      history.push({ pathname: `/trackingSystem` });
    }
  }, [shouldGoBack]);

  useEffect(() => {
    if (Object.keys(initialValues).length) {
      dispatch(initialize('TrackingSystem', initialValues));
    }
  }, [initialValues]);

  useEffect(() => {
    dispatch(
      trackingSystemForm({
        action: 'load',
        _id: params.id,
        comboType: ['TrackingSystemContact']
      })
    );
  }, [params.id, dispatch]);

  const onSubmit = useCallback(
    values => {
      dispatch(
        trackingSystemForm({
          action: params.id.toLowerCase() === 'new' ? 'add' : 'update',
          ...values,
          tableName: 'TrackingSystem'
        })
      );
    },
    [params.id]
  );

  const trackingSystemContacts =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      trackingSystemContactData
    ) || [];

  return (
    <FormBase
      {...props}
      initialValues={initialValues}
      err={err}
      isDark={props.isDark}
      title={strings.TrackingSystem}
      subTitle={initialValues.TrackingSystemName}
      onSubmit={onSubmit}
      actionInfo={{
        action: trackingSystemForm,
        actionName: TRACKING_SYSTEM_FORM
      }}
      onFormCancel={() => {
        dispatch(reset('TrackingSystem'));
        history.push('/trackingSystem');
      }}
    >
      <Row sm="12" md="12" lg="12" style={{ width: '100%' }}>
        <BasicRowColGrid>
          <label htmlFor="trackingSystemName">
            {strings.TrackingSystemName}
          </label>
          <Field
            name="TrackingSystemName"
            component={InputField}
            type="text"
            placeholder={strings.TrackingSystemName}
            maxLength={50}
            onChange={onChange}
            validate={Validator.required}
          />
        </BasicRowColGrid>
      </Row>

      {/* Tracking System Contacts Grid */}
      <Col sm={12} xl={12} className="mb-2 p-0" style={{ width: '100%' }}>
        <TrackingSystemContact
          {...props}
          key={props.trackingSystemId}
          baseFilter={props.baseFilter}
          onDblClick={props.onDblClick}
          handleAdd={props.handleAdd}
          showAddButton={props.showAddButton}
        />
      </Col>
    </FormBase>
  );
});

const TrackingSystemReduxForm = reduxForm({
  form: 'TrackingSystem',
  enableReinitialize: false,
  destroyOnUnmount: true,
  keepValues: true
})(TrackingSystemForm);

const TrackingSystemFormPanel = props => {
  const history = useHistory();
  const params = useParams();
  const dispatch = useDispatch();

  const trackingSystemId = params.id;
  const returnUrl = `/trackingSystem/${trackingSystemId}`;

  // Clear state when navigating to a different tracking system
  useEffect(() => {
    // Clear both tracking system form data and contact list data
    dispatch(trackingSystemFormReset());
    dispatch(trackingSystemContactListReset());
  }, [trackingSystemId, dispatch]);

  // Create filter to show only contacts belonging to this tracking system
  // Memoize to prevent unnecessary re-renders
  const baseFilter = useMemo(() => {
    if (
      trackingSystemId &&
      trackingSystemId.toLowerCase() !== consts.newString
    ) {
      return { TrackingSystem: trackingSystemId };
    }
    return { _id: null }; // New tracking system - show nothing
  }, [trackingSystemId]);

  // Fetch contacts when tracking system data is loaded
  useEffect(() => {
    if (
      trackingSystemId &&
      trackingSystemId.toLowerCase() !== consts.newString
    ) {
      dispatch(
        trackingSystemContactList({
          filters: baseFilter,
          action: 'list',
          limit: 25,
          skip: 0,
          sort: { sort: -1 },
          comboType: ['CountryCode']
        })
      );
    }
  }, [trackingSystemId, baseFilter, dispatch]);

  const onDblClick = (rowIdx, row) => {
    const searchParams = new URLSearchParams();
    searchParams.set('returnUrl', returnUrl);
    searchParams.set('trackingSystemId', trackingSystemId);

    history.push({
      pathname: `/trackingSystemContact/${row._id}`,
      search: searchParams.toString()
    });
  };

  const handleAddContact = () => {
    const searchParams = new URLSearchParams();
    searchParams.set('returnUrl', returnUrl);
    searchParams.set('trackingSystemId', trackingSystemId);

    history.push({
      pathname: `/trackingSystemContact/new`,
      search: searchParams.toString()
    });
  };

  // Determine if we should show the Add Contact button
  // Only show it for existing tracking systems (not 'new')
  const isNewTrackingSystem =
    !trackingSystemId || trackingSystemId.toLowerCase() === consts.newString;

  return (
    <Row style={{ display: 'flex' }}>
      <Col sm={12} xl={12} className="mb-2 p-0" style={{ flex: 1 }}>
        <TrackingSystemReduxForm
          {...props}
          trackingSystemId={trackingSystemId}
          baseFilter={baseFilter}
          onDblClick={onDblClick}
          handleAdd={isNewTrackingSystem ? undefined : handleAddContact}
          showAddButton={!isNewTrackingSystem}
        />
      </Col>
    </Row>
  );
};

export default TrackingSystemFormPanel;
